import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formatISO, isAfter, startOfDay, subDays } from "date-fns";
import { Box, Button, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DatePicker } from "@mui/x-date-pickers";
import { useFormik } from "formik";
import { get, orderBy } from "lodash";
import queryString from "query-string";
import * as Yup from "yup";

import { Page, ScreenHeader } from "@APP/components";
import { NUMBER_OF_FIRST_PAGE_IN_TABLE_WITH_PAGINATION, commonFormatDate } from "@APP/constants";
import { useAlert, useHandleErrorCodes } from "@APP/hooks";
import { AppState, hideLoader, setRTPS, setRtpFrom, setRtpTo, showLoader } from "@APP/redux";
import { API } from "@APP/services";
import { filterToolbarStyles } from "@APP/styles";
import { AdminRTPFilter, RTP, RtpSortBy, SortType } from "@APP/types";
import { formatErrorMessage } from "@APP/utils";

import RTPDashboardList from "./RTPDashboardList";

export type RTPWithDisplaying = RTP & { hidden: boolean };

enum RtpSortPaths {
  dueDateTime = "receivable.dueDateTime",
  createdAt = "createdAt",
  createdAtTime = "createdAt",
  amount = "amount.amount",
  status = "status",
  email = "supplier.primaryContact.email",
  firstDeliveryDate = "deliveryDates",
  firstDeliveryTime = "deliveryDates",
  channel = "channel",
  tip = "tipAmount.amount",
}

export const adminRTPValidationSchema = Yup.object().shape({
  from: Yup.date().required("Start date is required"),
  to: Yup.date().test("to", "End date must be after start date", (value, context) => {
    if (!value) return false;
    // start date can be the same as end date
    if (value.getTime() === context.parent.from.getTime()) return true;
    if (!context.parent.from) return false;

    return isAfter(value, context.parent.from);
  }),
});

const DATE_PRESETS = ["Today", "Last 7 days", "Last 30 days", "Custom"];

const rangeFor = (label: string) => {
  const now = new Date();
  if (label === "Last 7 days") return { from: formatISO(subDays(now, 7)), to: formatISO(now) };
  if (label === "Last 30 days") return { from: formatISO(subDays(now, 30)), to: formatISO(now) };

  // "Today" (and the "Custom" base value) start at the beginning of today
  return { from: formatISO(startOfDay(now)), to: formatISO(now) };
};

const RTPDashboardView = () => {
  const alert = useAlert();
  const dispatch = useDispatch();
  const classes = filterToolbarStyles();
  const handleErrorCodes = useHandleErrorCodes();
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
  const [customDatePicker, setCustomDatePicker] = useState(false);
  const [entries, setEntries] = useState(25);
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [lastPage, setLastPage] = useState(0);
  const [rtpSort, setRtpSort] = useState<RtpSortBy>(RtpSortBy.createdAt);
  const [sortedRtps, setSortedRtps] = useState<RTPWithDisplaying[]>([]);
  const [sortType, setSortType] = useState<SortType>(SortType.asc);
  const [fromLabel, setFromLabel] = useState<string>("");
  const { data, from, to } = useSelector(
    ({
      userAdmin: {
        rtpDashboard: { from, to, data },
      },
    }: AppState) => ({
      from,
      to,
      data,
    }),
  );

  const appendHiddenToRtp = (rtps: RTP[]): RTPWithDisplaying[] =>
    rtps.map((rtp) => ({ ...rtp, hidden: false }));

  useEffect(() => {
    !!data.length && setSortedRtps(appendHiddenToRtp(data));
  }, []);

  const getRtpsInDateRangeRecursively = async (
    filter: AdminRTPFilter,
    page = 0,
    prevRtps: RTP[] = [],
  ): Promise<RTP[]> => {
    const { data: rtpData, links, meta } = await API.getRTPSinDateRange(filter, { page });
    const rtps = [...prevRtps, ...rtpData] as RTP[];

    if (links.next) {
      return getRtpsInDateRangeRecursively(filter, page + 1, rtps);
    }
    const { query } = queryString.parseUrl(links?.last ?? "");
    setTotalItems(meta.totalItems);
    if (meta.totalItems) {
      if (meta.totalItems <= entries) {
        setLastPage(1);
      } else {
        if (query.page) {
          const lastPageIndex = Math.ceil(meta.totalItems / entries);
          setLastPage(Math.floor(lastPageIndex));
        }
      }
    }

    return rtps;
  };

  const fetchRTPs = async ({ from, to }: { from: string; to: string }) => {
    try {
      dispatch(showLoader());
      const rtpsData = await getRtpsInDateRangeRecursively({ from, to });

      dispatch(setRTPS(rtpsData));
      setSortedRtps(appendHiddenToRtp(rtpsData));
    } catch (error: any) {
      const errorData = error?.response?.data;
      const isHandled = handleErrorCodes(errorData?.errorCode);

      if (!isHandled) return;
      alert.open("Error", formatErrorMessage(error));
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleSorting = (by: RtpSortBy) => {
    const newSortType = by === rtpSort && sortType === SortType.asc ? SortType.desc : SortType.asc;

    if (by === rtpSort) {
      // Reverse the array manually
      const reversedRtps = [...sortedRtps].reverse();
      setSortedRtps(reversedRtps);
    } else {
      const rtpSortField = RtpSortPaths[by];
      const rtpsWithoutSortingField = sortedRtps.filter((rtp) => !get(rtp, rtpSortField));
      const rtpsWithSortingField = sortedRtps.filter((rtp) => get(rtp, rtpSortField));

      const sortedRtpsWithSortingField = orderBy(
        rtpsWithSortingField,
        (rtp) => get(rtp, rtpSortField),
        newSortType,
      );

      setSortedRtps([...sortedRtpsWithSortingField, ...rtpsWithoutSortingField]);
    }

    setRtpSort(by);
    setSortType(newSortType);
  };

  const searchData = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.toLowerCase();
      if (!value) {
        setSortedRtps(sortedRtps.map((rtp) => ({ ...rtp, hidden: false })));

        return;
      }

      const filteredRtps = sortedRtps.map((rtp) => {
        const {
          amount: { amount },
          createdAt,
          status,
          supplier: { companyInfo: { email = "", name = "" } = {} },
        } = rtp;

        const filterOn = [createdAt, amount, status, email, name];
        if (filterOn.some((filter) => filter?.toString().toLowerCase().includes(value))) {
          return { ...rtp, hidden: false };
        }

        return { ...rtp, hidden: true };
      });

      setSortedRtps(filteredRtps);
    },
    [sortedRtps],
  );

  const handleOnEntriesChange = (entries: number) => {
    setPage(NUMBER_OF_FIRST_PAGE_IN_TABLE_WITH_PAGINATION);
    setEntries(entries);
    setLastPage(Math.ceil(totalItems / entries));
  };

  const clearSearchFilter = () =>
    setSortedRtps(sortedRtps.map((rtp) => ({ ...rtp, hidden: false })));

  const handleOnSubmit = ({ from, to }: { from: string; to: string }) => {
    dispatch(setRtpFrom(from));
    dispatch(setRtpTo(to));
    fetchRTPs({ from, to });
    setSubmitting(false);
    setPage(0);
  };

  const {
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    touched,
    values,
    setFieldValue,
    resetForm,
    setSubmitting,
  } = useFormik({
    initialValues: {
      from: from,
      to: to,
      username: "",
    },
    validationSchema: adminRTPValidationSchema,
    onSubmit: handleOnSubmit,
  });

  useEffect(() => {
    const from = formatISO(startOfDay(new Date()));
    const to = formatISO(new Date());
    fetchRTPs({ from, to });
    setFromLabel("Today");
  }, []);

  const orgPagination = { page: page, entries: entries };

  const fetchSearchResults = async (fromLabel: string) => {
    dispatch(showLoader());
    try {
      if (entries !== 25) {
        showAlert(
          "Search Error",
          "There are too many records for this search criteria. Please update the search to be more specific.",
        );

        return;
      }

      const userNameRegex = /@[^\s@]*\.com\b/;
      const isEmailFormat = userNameRegex.test(values.username);

      if (isEmailFormat) {
        await handleUserNameSearch(fromLabel);
      } else {
        await handleOrganizationSearch(fromLabel);
      }
    } catch (error) {
      showAlert("Error", formatErrorMessage(error));
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleUserNameSearch = async (fromLabel: string) => {
    dispatch(showLoader());
    try {
      const fromDate = fromLabel === "Today" ? formatISO(startOfDay(new Date())) : from;

      const searchedData = await API.getPaymentsFromUserName(fromDate, values.username);
      if (searchData.length) {
        setSortedRtps(searchedData);
      } else {
        setSortedRtps([]);
      }
    } catch (error) {
      showAlert("Error", formatErrorMessage(error));
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleOrganizationSearch = async (fromLabel: string) => {
    dispatch(showLoader());
    try {
      const orgData = await API.getSearchedOrganisations(
        { orgname: values.username },
        orgPagination,
      );

      const orgIds = orgData.data.map((org) => org.orgId);

      if (!orgIds.length) {
        setSortedRtps([]);

        return;
      }

      const fromDate = fromLabel === "Today" ? formatISO(startOfDay(new Date())) : from;

      const searchedData = await API.getPaymentsFromUserOrg(fromDate, orgIds);
      if (searchData.length) {
        setSortedRtps(searchedData);
      }
    } catch (error) {
      showAlert("Error", formatErrorMessage(error));
    } finally {
      dispatch(hideLoader());
    }
  };

  const showAlert = (title: string, message: string) => {
    alert.open(title, message, [{ text: "Okay" }]);
  };

  const clearResults = () => {
    resetForm();
    window.location.reload();
  };

  return (
    <Page title="RTP Dashboard">
      <ScreenHeader title="RTP Dashboard" />
      <form onSubmit={handleSubmit}>
        <Box className={classes.toolbar}>
          <Box className={classes.topRow}>
            <div className={classes.segment} role="group" aria-label="Created date range">
              {DATE_PRESETS.map((label) => {
                const isCustom = label === "Custom";

                return (
                  <button
                    key={label}
                    type={isCustom ? "button" : "submit"}
                    aria-pressed={fromLabel === label}
                    className={`${classes.segmentButton} ${
                      fromLabel === label ? classes.segmentButtonActive : ""
                    }`}
                    onClick={() => {
                      const { from, to } = rangeFor(label);
                      setFieldValue("from", from);
                      setFieldValue("to", to);
                      setFieldValue("username", "");
                      setFromLabel(label);
                      setCustomDatePicker(isCustom);
                    }}>
                    {label}
                  </button>
                );
              })}
            </div>

            <Box className={classes.searchGroup}>
              <TextField
                className={classes.search}
                size="small"
                name="username"
                placeholder="Search by username or organisation"
                value={values.username}
                error={Boolean(touched.username && errors.username)}
                helperText={touched.username && errors.username}
                onChange={handleChange}
                onBlur={(e) => {
                  const trimmedValue = e.target.value.trim();
                  if (trimmedValue !== e.target.value) {
                    setFieldValue("username", trimmedValue);
                  }
                  handleBlur(e);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (values.username?.trim()) fetchSearchResults(fromLabel);
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
                inputProps={{ "aria-label": "Search RTPs by username or organisation" }}
              />
              <Button
                variant="contained"
                color="primary"
                disabled={!values.username?.trim() || isSubmitting}
                onClick={() => fetchSearchResults(fromLabel)}>
                {isSubmitting ? "Searching..." : "Search"}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                disabled={!values.username?.trim() || isSubmitting}
                onClick={clearResults}>
                Clear
              </Button>
            </Box>
          </Box>

          {customDatePicker && (
            <Box className={classes.customRange}>
              <DatePicker
                label="From"
                value={values.from}
                inputFormat={commonFormatDate}
                disableFuture
                open={startDatePickerOpen}
                onOpen={() => setStartDatePickerOpen(true)}
                onClose={() => setStartDatePickerOpen(false)}
                onChange={(value) => setFieldValue("from", value)}
                renderInput={(props) => (
                  <TextField
                    {...props}
                    size="small"
                    onClick={() => setStartDatePickerOpen(true)}
                    error={Boolean(touched.from && errors.from)}
                    helperText={touched.from && errors.from}
                  />
                )}
              />
              <DatePicker
                label="To"
                value={values.to}
                inputFormat={commonFormatDate}
                disableFuture
                open={endDatePickerOpen}
                onOpen={() => setEndDatePickerOpen(true)}
                onClose={() => setEndDatePickerOpen(false)}
                onChange={(value) => setFieldValue("to", value)}
                renderInput={(props) => (
                  <TextField
                    {...props}
                    size="small"
                    onClick={() => setEndDatePickerOpen(true)}
                    error={Boolean(touched.to && errors.to)}
                    helperText={touched.to && errors.to}
                  />
                )}
              />
              <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                Apply
              </Button>
            </Box>
          )}
        </Box>

        <RTPDashboardList
          data={sortedRtps}
          rtpSort={rtpSort}
          sortType={sortType}
          entries={entries}
          page={page}
          lastPage={lastPage}
          setEntries={setEntries}
          setPage={setPage}
          clearSearchFilter={clearSearchFilter}
          searchData={searchData}
          handleSorting={handleSorting}
          handleOnEntriesChange={handleOnEntriesChange}
        />
      </form>
    </Page>
  );
};

export default RTPDashboardView;
